/**
 * Camada 1 · tabela.
 *
 * Tabela de verdade, com <table>, e nao div empilhada com aparencia de tabela. O leitor
 * de tela anuncia "coluna 3 de 7, linha 12" a partir da marcacao real, e nenhuma classe
 * de grid devolve essa informacao.
 *
 * O ESTADO VAZIO E OBRIGATORIO NO TIPO: sem dado, a tabela DIZ que nao ha dado, com o
 * motivo. Ela nao desenha linha de exemplo e nao some da tela. Numero em tela ou e real
 * ou e um traco.
 *
 * ---------------------------------------------------------------------------
 * A LINHA CLICAVEL: O CONTROLE MORA NA CELULA, E A LINHA CONTINUA SENDO LINHA
 * ---------------------------------------------------------------------------
 * ATE 09/09/2026 A LINHA CLICAVEL LEVAVA `role="button"` no proprio `<tr>`. A intencao
 * estava certa (sem teclado, a acao existe so para quem usa mouse) e o PAPEL escolhido
 * estava errado, porque papel explicito SUBSTITUI o implicito: aquele `<tr>` deixava de
 * ser `row`, e com ele iam embora a coluna, o cabecalho associado e a posicao na tabela.
 * Quem usa leitor de tela recebia uma pilha de botoes soltos onde havia uma tabela, e o
 * texto de cada celula perdia a pergunta que ele responde.
 *
 * E PIOR QUE PERDER A ESTRUTURA: a arvore fica invalida. `table` exige filhos `row`, e
 * `row` exige `cell`. Um `button` pendurado dentro do `rowgroup` quebra os dois lados, e e
 * exatamente o que as regras `aria-required-children` e `aria-required-parent` do axe
 * acusam.
 *
 * O PADRAO ADOTADO E O PUBLICADO, e nao uma invencao nossa:
 *
 * · O `<tr>` NAO recebe role, tabIndex nem onKeyDown, e continua sendo `row`. `ARIA in
 *   HTML` (W3C) so admite `row` para `<tr>` dentro de uma `table`.
 * · O CONTROLE REAL VAI DENTRO DE UMA CELULA, na primeira, e o texto dela e o nome
 *   acessivel. E a orientacao do GOV.UK Design System para linha clicavel ("nao
 *   transforme a linha inteira em link; ponha o link na primeira celula"), e a mesma
 *   conclusao a que Adrian Roselli chega nos artigos sobre linha de tabela acionavel.
 * · O clique no resto da linha vira CONVENIENCIA DE PONTEIRO, e nao a unica porta.
 *
 * O CAMINHO QUE NAO FOI TOMADO, e por que: o `grid` do W3C ARIA Authoring Practices Guide
 * e o unico jeito sancionado de tornar a TABELA INTEIRA um widget, e ele cobra o pacote
 * completo, com `role="grid"`, `gridcell` e navegacao por setas com tabindex rotativo.
 * Isso mudaria como TODA tabela da plataforma se comporta no teclado, para resolver um
 * caso de "abrir a linha", e o custo nao paga.
 *
 * O QUE ISSO CUSTA, e fica dito: o `Tab` agora para uma vez por linha, no botao da
 * primeira celula. Antes ele parava uma vez por linha tambem, no `<tr>`. A contagem de
 * paradas nao mudou; o que mudou e que a parada agora tem nome e papel.
 */

import type { ReactNode } from 'react';

export interface Coluna<T> {
  readonly chave: string;
  /** Cabecalho EM PORTUGUES, que e tambem a chave do dicionario de traducao. */
  readonly titulo: string;
  /** Como desenhar a celula. Recebe a linha inteira, e nao so o valor, porque celula
   *  costuma precisar de outro campo da mesma linha para decidir o que mostrar. */
  readonly celula: (linha: T) => ReactNode;
  /** Alinhamento a direita para numero, que e como se le coluna de valor. */
  readonly numerica?: boolean;
}

export interface TabelaProps<T> {
  readonly colunas: readonly Coluna<T>[];
  readonly linhas: readonly T[];
  /** Chave estavel por linha. Indice do array nao serve: ao ordenar ou filtrar, o React
   *  reaproveita a linha errada e o estado de uma aparece em outra. */
  readonly chaveDaLinha: (linha: T) => string;
  /** O que dizer quando nao ha linhas, e por que. Obrigatorio de proposito. */
  readonly vazio: string;
  readonly aoClicarLinha?: (linha: T) => void;
  readonly rotulo: string;
}

export function Tabela<T>({
  colunas,
  linhas,
  chaveDaLinha,
  vazio,
  aoClicarLinha,
  rotulo,
}: TabelaProps<T>) {
  if (linhas.length === 0) {
    return (
      <div className="cx-cartao cx-vazio" role="status">
        {vazio}
      </div>
    );
  }

  return (
    // O contêiner rola sozinho no eixo horizontal. Sem isso, uma tabela larga empurra a
    // pagina inteira e o corpo passa a rolar de lado no celular, que e o defeito que a
    // regra de tres larguras pega.
    <div className="cx-cartao overflow-x-auto">
      <table className="w-full border-collapse text-cx-sm">
        <caption className="cx-so-leitor">{rotulo}</caption>
        <thead>
          <tr className="border-b border-borda">
            {colunas.map((c) => (
              <th
                key={c.chave}
                scope="col"
                className={[
                  'px-cx-4 py-cx-3 font-medium text-texto-suave',
                  c.numerica ? 'text-right' : 'text-left',
                ].join(' ')}
              >
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <tr
              key={chaveDaLinha(linha)}
              // O CLIQUE NA LINHA E CONVENIENCIA DE PONTEIRO, e so isso. Quem alcanca a acao
              // por teclado e por leitor de tela e o botao real da PRIMEIRA CELULA, abaixo.
              //
              // A LINHA NAO RECEBE `role`, `tabIndex` NEM `onKeyDown`, e isso e a correcao de
              // 09/09/2026. Ver o cabecalho deste arquivo: papel explicito substitui o
              // implicito, e a linha deixava de ser `row`.
              onClick={aoClicarLinha ? () => aoClicarLinha(linha) : undefined}
              className={[
                'border-b border-borda last:border-0',
                aoClicarLinha ? 'cursor-pointer hover:bg-superficie-afundada' : '',
              ].join(' ')}
            >
              {colunas.map((c, indice) => (
                <td
                  key={c.chave}
                  className={['px-cx-4 py-cx-3 text-texto', c.numerica ? 'text-right tabular-nums' : ''].join(' ')}
                >
                  {aoClicarLinha && indice === 0 ? (
                    <button
                      type="button"
                      // `stopPropagation` PORQUE O CLIQUE SOBE ATE A LINHA: sem ele, o
                      // ponteiro dispararia a acao DUAS vezes, uma pelo botao e outra pelo
                      // `onClick` do `<tr>`.
                      onClick={(evento) => {
                        evento.stopPropagation();
                        aoClicarLinha(linha);
                      }}
                      // O RESET E OBRIGATORIO: o navegador da ao `<button>` fonte, tamanho e
                      // cor proprios, e sem `[font:inherit]` a primeira celula sairia com
                      // tipografia diferente das outras cinco.
                      //
                      // O ANEL DE FOCO NAO E DECLARADO AQUI: ele e global, no `:focus-visible`
                      // de `packages/ui/src/styles.css:43`, e e a mesma decisao que
                      // `Superficie.tsx:181` ja registra.
                      className={[
                        'w-full cursor-pointer bg-transparent p-0 [color:inherit] [font:inherit]',
                        c.numerica ? 'text-right' : 'text-left',
                      ].join(' ')}
                    >
                      {c.celula(linha)}
                    </button>
                  ) : (
                    c.celula(linha)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
